// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The voice pool, with priority stealing (M3-20, 07 §2).
//
// A pinball machine in full multiball genuinely asks for forty simultaneous sounds, and no
// device is going to give you forty. So the pool is fixed and something has to lose: the
// question a voice pool answers is *what*, and answering it badly is very audible.
//
// The order — speech, knocker, jackpot, solenoid, ball impact, ball roll, ambience — is a
// statement about what a player is listening *for*. A callout carries information nothing else
// conveys, so it outranks everything. A knocker is a free game; missing one is missing the best
// news the machine has. Ball roll is near the bottom because it is a continuous bed: losing one
// ball's roll for a moment during a six-ball frenzy is imperceptible, and losing the jackpot
// stab that was stolen for is not.
//
// Two decisions that are easy to get wrong:
//
//   * **Equal priority steals, oldest first.** Denying instead sounds broken during multiball:
//     the newest impact is the one the player just caused and is watching, while the voice it
//     would have replaced is thirty milliseconds into a forty-millisecond tick and already
//     inaudible. Keeping the old one and dropping the new one is exactly backwards.
//   * **A slot carries a generation.** A one-shot releases itself when it finishes, and by then
//     its slot may have been stolen and handed to something else. Releasing on the id alone
//     would cut off the sound that took its place — a bug that only appears under load, only
//     sometimes, and sounds like the audio engine dropping notes at random.
//
// No clock. Age is an acquisition counter, so "oldest" is exact, the pool is pure, and it does
// not need the audio context to decide anything.

import type { RenderTier } from '../world/tiers'

/** Highest priority first. Index is the rank, so lower is more important. */
export const VOICE_PRIORITIES = [
  'speech',
  'knocker',
  'jackpot',
  'solenoid',
  'ballImpact',
  'ballRoll',
  'ambience',
] as const

export type VoicePriority = (typeof VOICE_PRIORITIES)[number]

/** 0 is the most important. */
export function priorityRank(priority: VoicePriority): number {
  return VOICE_PRIORITIES.indexOf(priority)
}

/**
 * Voices per tier.
 *
 * Its own table rather than a field on `TierBudget`, which is about pixels and lights: a device
 * can be render-limited and not audio-limited, and the two budgets are spent by different
 * layers. The tier *vocabulary* is shared, because having two of those is how they come to
 * disagree.
 */
export const TIER_VOICES: Readonly<Record<RenderTier, number>> = {
  xr: 24,
  high: 32,
  medium: 24,
  low: 16,
}

/**
 * A claim on a slot.
 *
 * Opaque on purpose: the holder gives it back and never reads it. `generation` is what makes a
 * stale release harmless.
 */
export interface VoiceHandle {
  readonly slot: number
  readonly generation: number
  readonly priority: VoicePriority
}

interface Slot {
  generation: number
  active: boolean
  rank: number
  /** Acquisition order. Monotonic across the pool's life. */
  age: number
  stop: (() => void) | undefined
}

export class VoicePool {
  readonly size: number

  private readonly slots: Slot[]
  private acquisitions = 0

  /** Voices cut short to make room. A steady climb means the pool is too small. */
  stolen = 0
  /** Requests refused because everything playing mattered more. */
  denied = 0

  constructor(size: number) {
    this.size = Math.max(1, Math.floor(size))
    this.slots = []
    for (let i = 0; i < this.size; i++) {
      this.slots.push({ generation: 0, active: false, rank: 0, age: 0, stop: undefined })
    }
  }

  /** How many voices are sounding. */
  get active(): number {
    let n = 0
    for (const slot of this.slots) if (slot.active) n++
    return n
  }

  /** How many are sounding at a given priority. For diagnostics and for tests. */
  activeAt(priority: VoicePriority): number {
    const rank = priorityRank(priority)
    let n = 0
    for (const slot of this.slots) if (slot.active && slot.rank === rank) n++
    return n
  }

  /**
   * Claim a slot, or nothing if everything playing outranks the request.
   *
   * `stop` is called if the voice is stolen or the pool is cleared, and is the caller's only
   * notice that its sound is over. It must be idempotent-safe: it is never called twice for one
   * acquisition, but it may be called at any moment after `acquire` returns.
   */
  acquire(priority: VoicePriority, stop: () => void): VoiceHandle | undefined {
    const rank = priorityRank(priority)

    let chosen = this.freeSlot()
    if (chosen === undefined) {
      chosen = this.victimFor(rank)
      if (chosen === undefined) {
        this.denied++
        return undefined
      }
      const slot = this.slots[chosen]!
      slot.active = false
      const stopVictim = slot.stop
      slot.stop = undefined
      this.stolen++
      // Called before the slot is reassigned, so a `stop` that itself calls `release` finds a
      // slot it no longer owns — the generation bump below is what makes that harmless, and
      // doing it in this order is what makes the bump reachable.
      stopVictim?.()
    }

    const slot = this.slots[chosen]!
    slot.generation++
    slot.active = true
    slot.rank = rank
    slot.age = this.acquisitions++
    slot.stop = stop
    return { slot: chosen, generation: slot.generation, priority }
  }

  /**
   * Give a slot back. Returns false if the handle is stale, which is not an error.
   *
   * A one-shot that finishes after being stolen releases a slot somebody else is using, and
   * this is where that stops being a problem.
   */
  release(handle: VoiceHandle): boolean {
    const slot = this.slots[handle.slot]
    if (slot === undefined || !slot.active || slot.generation !== handle.generation) return false
    slot.active = false
    slot.stop = undefined
    return true
  }

  /** Stop everything. Teardown, and the blackout rule. */
  releaseAll(): void {
    for (const slot of this.slots) {
      if (!slot.active) continue
      slot.active = false
      const stop = slot.stop
      slot.stop = undefined
      slot.generation++
      stop?.()
    }
  }

  private freeSlot(): number | undefined {
    for (let i = 0; i < this.slots.length; i++) if (!this.slots[i]!.active) return i
    return undefined
  }

  /**
   * The worst voice playing, provided it is not more important than the request.
   *
   * Worst rank wins; ties go to the oldest. Strictly-better voices are never candidates, which
   * is the whole guarantee: a ball roll can never silence a callout, however many balls there
   * are.
   */
  private victimFor(rank: number): number | undefined {
    let best: number | undefined
    let bestRank = -1
    let bestAge = Number.POSITIVE_INFINITY
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!
      if (!slot.active || slot.rank < rank) continue
      if (slot.rank > bestRank || (slot.rank === bestRank && slot.age < bestAge)) {
        best = i
        bestRank = slot.rank
        bestAge = slot.age
      }
    }
    return best
  }
}
